import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart3, Users, TrendingUp, Target, Cpu, Shield, ArrowRight, Loader2, AlertCircle, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import BackNavigation from "@/components/BackNavigation";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend
} from "recharts";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Design system: ONLY Red + Blue shades
const INDEX_COLORS = {
  trust: "#1E3A8A",
  alignment: "#2563EB",
  readiness: "#3B82F6",
  ai_capability: "#60A5FA",
  resilience: "#93C5FD"
};

// Industry colors - Blue shades only
const INDUSTRY_COLORS = {
  "Manufacturing": "#1E3A8A",
  "Logistics": "#1E40AF",
  "Security": "#1D4ED8",
  "Healthcare": "#2563EB",
  "Technology": "#3B82F6",
  "Government": "#60A5FA",
  "Education": "#93C5FD",
  "Construction": "#BFDBFE",
  "Nonprofit": "#DBEAFE",
  "Other": "#EFF6FF"
};

export default function ObservatoryDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [gwiiData, setGwiiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashboardRes, gwiiRes] = await Promise.all([
        axios.get(`${API}/observatory/dashboard`),
        axios.get(`${API}/observatory/gwii`)
      ]);
      setDashboardData(dashboardRes.data);
      setGwiiData(gwiiRes.data);
      setLoading(false);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setError("Failed to load dashboard data");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Card className="max-w-md border-[#E5E7EB]">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-[#991B1B] mx-auto mb-4" />
            <h2 className="font-heading text-xl font-semibold text-[#111827] mb-2">Error Loading Dashboard</h2>
            <p className="text-[#4B5563] mb-4">{error}</p>
            <Button onClick={fetchData} className="bg-[#1E3A8A] text-white hover:bg-[#1E40AF]">Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasData = dashboardData?.has_data;

  // Prepare chart data - Blue shades
  const indicesData = dashboardData?.indices_averages 
    ? Object.entries(dashboardData.indices_averages).map(([key, value]) => ({
        name: key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        value: value,
        fill: INDEX_COLORS[key] || '#3B82F6'
      }))
    : [];

  const industryBenchmarkData = Object.entries(dashboardData?.industry_benchmarks || {}).map(([industry, avg], index) => ({
    industry: industry.length > 10 ? industry.slice(0, 10) + '...' : industry,
    fullName: industry,
    average: avg,
    fill: Object.values(INDEX_COLORS)[index % 5]
  }));

  const sectorData = dashboardData?.sector_breakdown 
    ? Object.entries(dashboardData.sector_breakdown).map(([sector, data], index) => ({
        name: sector.length > 12 ? sector.slice(0, 12) + '...' : sector,
        fullName: sector,
        value: data.count,
        fill: Object.values(INDEX_COLORS)[index % 5]
      }))
    : [];

  const trendsData = dashboardData?.trends?.map((t, index) => ({
    index: index + 1,
    score: t.wii_score,
    industry: t.industry
  })).reverse() || [];

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8 sm:py-12" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <BackNavigation fallbackPath="/observatories/global" fallbackLabel="Global Observatory" />
        </div>

        {/* Header */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <span className="text-sm font-medium text-[#991B1B] uppercase tracking-wider">Global Analytics</span>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-1 mb-2" style={{ color: '#991B1B' }} data-testid="dashboard-title">
                Workforce Intelligence Dashboard
              </h1>
              <p className="text-[#4B5563]">
                Global Workforce Intelligence Index (GWII) and aggregated analytics.
              </p>
            </div>
            <Link to="/juswork/observatory/survey">
              <Button className="gap-2 bg-[#991B1B] text-white hover:bg-[#7F1D1D]" data-testid="contribute-button">
                Contribute Data
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* GWII Summary - Blue card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-8"
        >
          <Card className="bg-[#1E3A8A] text-white border-0 shadow-xl" data-testid="gwii-card">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <Globe className="w-8 h-8 mx-auto mb-2 text-white/60" />
                  <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Global WII (GWII)</p>
                  <p className="font-mono text-5xl font-bold">{gwiiData?.global_wii || dashboardData?.global_wii}</p>
                  <p className="text-white/60 text-sm mt-1">{dashboardData?.total_responses || 0} responses</p>
                </div>
                <div className="border-l border-r border-white/10 px-8">
                  <p className="text-white/60 text-xs uppercase tracking-wider mb-4 text-center">Index Averages</p>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(dashboardData?.indices_averages || {}).slice(0, 4).map(([key, value]) => (
                      <div key={key} className="text-center">
                        <p className="font-mono text-xl font-bold text-blue-200">{value}</p>
                        <p className="text-white/50 text-xs capitalize">{key.replace('_', ' ')}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Resilience Index</p>
                  <p className="font-mono text-4xl font-bold text-blue-200">{dashboardData?.indices_averages?.resilience || '6.6'}</p>
                  <p className="text-white/60 text-sm mt-2">Organizational Resilience</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Metrics Grid - Blue shades */}
        <motion.div
          className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          {[
            { icon: Users, label: "Trust", value: dashboardData?.indices_averages?.trust || '6.8', color: INDEX_COLORS.trust },
            { icon: Target, label: "Alignment", value: dashboardData?.indices_averages?.alignment || '6.6', color: INDEX_COLORS.alignment },
            { icon: TrendingUp, label: "Readiness", value: dashboardData?.indices_averages?.readiness || '6.7', color: INDEX_COLORS.readiness },
            { icon: Cpu, label: "AI Capability", value: dashboardData?.indices_averages?.ai_capability || '6.5', color: INDEX_COLORS.ai_capability },
            { icon: Shield, label: "Resilience", value: dashboardData?.indices_averages?.resilience || '6.6', color: INDEX_COLORS.resilience }
          ].map((metric, index) => (
            <Card key={index} className="border-[#E5E7EB] bg-white" data-testid={`metric-${metric.label.toLowerCase()}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${metric.color}15` }}>
                    <metric.icon className="w-5 h-5" style={{ color: metric.color }} />
                  </div>
                  <div>
                    <p className="text-xs text-[#4B5563]">{metric.label}</p>
                    <p className="font-mono text-xl font-bold" style={{ color: metric.color }}>{metric.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </motion.div>

        {!hasData ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="border-[#E5E7EB] bg-white mb-8" data-testid="empty-state">
              <CardContent className="p-12 text-center">
                <BarChart3 className="w-16 h-16 text-[#E5E7EB] mx-auto mb-6" />
                <h2 className="font-heading text-2xl font-semibold text-[#111827] mb-3">
                  No Survey Data Yet
                </h2>
                <p className="text-[#4B5563] mb-6 max-w-md mx-auto">
                  Be the first to contribute. The dashboard displays seeded benchmark data below.
                </p>
                <Link to="/juswork/observatory/survey">
                  <Button className="gap-2 bg-[#1E3A8A] text-white hover:bg-[#1E40AF]">
                    Take the Diagnostic
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Average Indices - Blue shades */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="border-[#E5E7EB] bg-white h-full" data-testid="indices-chart">
                <CardHeader>
                  <CardTitle className="font-heading text-lg" style={{ color: '#991B1B' }}>Average Indices</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={indicesData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 12, fill: '#374151' }} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#374151' }} width={100} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1E3A8A', border: 'none', borderRadius: '6px' }}
                          labelStyle={{ color: 'white' }}
                          itemStyle={{ color: 'white' }}
                          formatter={(value) => [`${value} / 10`, 'Average']}
                        />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                          {indicesData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Responses by Sector - Blue shades */}
            {sectorData.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
              >
                <Card className="border-[#E5E7EB] bg-white h-full" data-testid="sector-chart">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg" style={{ color: '#991B1B' }}>Responses by Sector</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={sectorData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={90}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            labelLine={{ stroke: '#374151' }}
                          >
                            {sectorData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1E3A8A', border: 'none', borderRadius: '6px' }}
                            labelStyle={{ color: 'white' }}
                            itemStyle={{ color: 'white' }}
                            formatter={(value, name, props) => [value, props.payload.fullName]}
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        )}

        {/* Recent Trends - Blue line */}
        {trendsData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-8"
          >
            <Card className="border-[#E5E7EB] bg-white" data-testid="trends-chart">
              <CardHeader>
                <CardTitle className="font-heading text-lg" style={{ color: '#991B1B' }}>Recent Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="index" tick={{ fontSize: 12, fill: '#374151' }} />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 12, fill: '#374151' }} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1E3A8A', border: 'none', borderRadius: '6px' }}
                        labelStyle={{ color: 'white' }}
                        itemStyle={{ color: 'white' }}
                        formatter={(value, name, props) => [`${value}`, `WII (${props.payload.industry})`]}
                      />
                      <Line type="monotone" dataKey="score" stroke="#1E3A8A" strokeWidth={2} dot={{ fill: '#1E3A8A', strokeWidth: 2, r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Industry Benchmarks - Blue shades */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
        >
          <Card className="border-[#E5E7EB] bg-white" data-testid="benchmarks-chart">
            <CardHeader>
              <CardTitle className="font-heading text-lg" style={{ color: '#991B1B' }}>Industry Benchmarks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={industryBenchmarkData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="industry" tick={{ fontSize: 10, fill: '#374151' }} angle={-45} textAnchor="end" height={80} />
                    <YAxis domain={[0, 10]} tick={{ fontSize: 12, fill: '#374151' }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1E3A8A', border: 'none', borderRadius: '6px' }}
                      labelStyle={{ color: 'white' }}
                      itemStyle={{ color: 'white' }}
                      formatter={(value, name, props) => [`${value}`, props.payload.fullName]}
                    />
                    <Bar dataKey="average" name="Industry GWII" radius={[4, 4, 0, 0]}>
                      {industryBenchmarkData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
